// THREAD-READS: Slack's per-thread read state in the client. One cached list (`my/threads`, the
// Threads view's query) answers every screen: the sidebar's Threads count, the Threads page and
// the "n new" on a root's reply line in the stream (a followed thread with new replies is among
// the latest 200 by its last reply, so the page holds every one that can show "new"). Marking
// read, following and "Mark all read" patch that list first (optimistic: the dot and the count
// go at once) and reconcile with the server's answer; change pings (`thread.read`,
// `thread.follow_changed`, a new reply) refetch it in the background.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useOptimisticMutation } from "../../api/optimistic";
import { chatKeys, myThreadsQuery, type ThreadSummaryView } from "./chat-queries";
import type { ChatContext } from "./use-chat";

type ThreadsPage = { data: ThreadSummaryView[]; next_cursor: string | null };

/** A root's state for the viewer: unread replies, and whether it follows the thread. */
export type ThreadReadInfo = { unread: number; following: boolean };

/** Threads the viewer follows by root id (unread counts for the stream's reply lines). */
export function useThreadReads(chat: ChatContext): ReadonlyMap<string, ThreadReadInfo> {
  const threads = useQuery({
    ...myThreadsQuery(chat.api, chat.co),
    enabled: chat.meId !== null,
    retry: false,
  });
  return useMemo(
    () =>
      new Map(
        (threads.data?.data ?? []).map((t) => [
          t.root.id,
          { unread: t.unread_count, following: t.following },
        ]),
      ),
    [threads.data],
  );
}

/** The thread-read mutations, each optimistic over the `my/threads` cache. */
export function useThreadReadActions(chat: ChatContext) {
  const queryClient = useQueryClient();
  const { api, co } = chat;
  const key = chatKeys.threads(co);
  const patch = (fn: (rows: ThreadSummaryView[]) => ThreadSummaryView[]) =>
    queryClient.setQueriesData<ThreadsPage>({ queryKey: key }, (d) =>
      d ? { ...d, data: fn(d.data) } : d,
    );
  const cleared = (t: ThreadSummaryView): ThreadSummaryView => ({
    ...t,
    unread: false,
    unread_count: 0,
  });

  const read = useOptimisticMutation({
    keys: () => [key],
    apply: (v: { threadId: string; rootId: string }) =>
      patch((rows) => rows.map((t) => (t.root.id === v.rootId ? cleared(t) : t))),
    mutationFn: (v) =>
      api.client.threads.read({ params: { co, id: v.threadId, mid: v.rootId }, body: {} }),
    // A failed read is harmless (the dot comes back with the next refetch): no toast.
    onFailure: () => undefined,
    settle: () => [key],
  });

  const follow = useOptimisticMutation({
    keys: () => [key],
    // The row stays (marked) until the refetch drops it; the Threads page keeps its own copy so
    // the person can follow again from the same place.
    apply: (v: { threadId: string; rootId: string; following: boolean }) =>
      patch((rows) =>
        rows.map((t) => (t.root.id === v.rootId ? { ...t, following: v.following } : t)),
      ),
    mutationFn: (v) =>
      api.client.threads.follow({
        params: { co, id: v.threadId, mid: v.rootId },
        body: { following: v.following },
      }),
    settle: () => [key],
  });

  const readAll = useOptimisticMutation({
    keys: () => [key],
    apply: (_: undefined) => patch((rows) => rows.map(cleared)),
    mutationFn: () => api.client.threads.readAll({ params: { co } }),
    settle: () => [key],
  });

  // TanStack's `mutate` is stable; so are these (an effect may depend on `markRead`).
  const readMutate = read.mutate;
  const markRead = useCallback(
    (threadId: string, rootId: string) => readMutate({ threadId, rootId }),
    [readMutate],
  );
  return {
    markRead,
    setFollowing: (threadId: string, rootId: string, following: boolean) =>
      follow.mutate({ threadId, rootId, following }),
    markAllRead: () => readAll.mutate(undefined),
    readingAll: readAll.isPending,
  };
}
